import {
  ScannerSearchItem,
  isScannerSearchItem,
  isLinkingSearchItem,
  LinkingSearchItem,
  SearchItem,
  WatchlistSearchItem,
  isWatchlistSearchItem,
  SymbolSearchItem,
  isSymbolSearchItem,
  isHoldingSearchItem,
  isVerticalSearchItem,
  HoldingSearchItem,
  VerticalSearchItem,
} from '@benzinga/search-modules';
import { NotFunction } from '@benzinga/utils';
import { IconType } from 'react-icons/lib';

export interface VisualizationArgs<P = VisualizationArgsType> {
  args: NotFunction<P>;
  flightMode: boolean;
  onChange: (args: NotFunction<P> | ((args: NotFunction<P>) => NotFunction<P>)) => void;
  sources: GraphModuleItem[];
  onSourceChanged: (source: GraphModuleItem[]) => void;
}

export interface VisualizationArgsType<
  T extends BaseTrace = BaseTrace,
  L extends object = object,
  G extends object = object,
> {
  traces: T[];
  layout: L;
  global?: G;
}

export type ParentIndex = number;

export interface ButtonDeclarationItem<P extends VisualizationArgsType> {
  label: string;
  type: 'button';
  icon?: IconType;
  onClick: (args: P, path: ParentIndex) => P;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface SwitchDeclarationItem<P extends VisualizationArgsType> {
  label: string;
  type: 'switch';
  icon?: IconType;
  get: (args: P, path: ParentIndex) => boolean;
  set: (args: P, value: boolean, path: ParentIndex) => P;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface SliderDeclarationItem<P extends VisualizationArgsType> {
  label: string;
  type: 'slider';
  icon?: IconType;
  get: (args: P, path: ParentIndex) => [number, number];
  set: (args: P, value: [number, number], path: ParentIndex) => P;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface InputDeclarationItem<P extends VisualizationArgsType> {
  label: string;
  type: 'input';
  inputType: 'number' | 'percentage' | 'string';
  icon?: IconType;
  get: (args: P, path: ParentIndex) => number | string;
  set: (args: P, value: number | string, path: ParentIndex) => P;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface DropdownValue<T extends number | string> {
  value: T;
  label: React.ReactNode;
  icon?: IconType;
}

export interface DropdownDeclarationItem<P extends VisualizationArgsType, T extends number | string = string, D = any> {
  label: string;
  icon?: IconType;
  type: 'dropdown';
  allowNone?: boolean;
  options: ((args: P, path: ParentIndex, data: D) => DropdownValue<T>[]) | DropdownValue<T>[];

  set: (args: P, value: T | null, path: ParentIndex) => P;
  get: (args: P, path: ParentIndex) => T | null;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface ListDeclarationItem<P extends VisualizationArgsType> {
  getTitle: (args: P, path: ParentIndex) => string;
  setTitle: (args: P, path: ParentIndex, value: string) => P;
  getLength: (args: P) => number;
  onDelete: (args: P, path: ParentIndex) => P;
  id: string;
  type: 'list';
  minNumInList: number;
  args: Exclude<ArgsItem<P>, { type: 'list' } | { type: 'enum_list' }>[];
  isVisible?: (args: P, path: ParentIndex) => boolean;
}

export interface EnumListDeclarationItem<P extends VisualizationArgsType, T extends string = string> {
  getTitle: (args: P, path: ParentIndex) => string;
  setTitle: (args: P, path: ParentIndex, value: string) => P;
  getLength: (args: P) => number;
  getEnumType: (args: P, path: ParentIndex) => T;
  getOptions?: (args: P, path: ParentIndex) => T[] | undefined;
  onDelete: (args: P, path: ParentIndex) => P;
  id: string;
  type: 'enum_list';
  dropdownLabel: string;
  minNumInList: number;
  args: {
    enum: T;
    set: (args: P, path: ParentIndex) => P;
    label: string;
    icon?: IconType;
    args: Exclude<ArgsItem<P>, { type: 'list' } | { type: 'enum_list' }>[];
  }[];
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface DropdownAndButtonDeclarationItem<
  P extends VisualizationArgsType,
  T extends number | string = string,
  D = any,
> {
  label: string;
  type: 'dropdownAndButton';
  options: ((args: P, path: ParentIndex, data: D) => DropdownValue<T>[]) | DropdownValue<T>[];
  allowNone?: boolean;
  icon?: IconType;
  set: (args: P, value: T | null, path: ParentIndex) => P;
  get: (args: P, path: ParentIndex) => T | null;
  buttonIcon: React.ReactNode;
  onButtonClick: (args: P, path: ParentIndex) => P;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
}

export interface ButtonModal<P extends VisualizationArgsType, T extends number | string = string> {
  label: string;
  component: React.ReactNode;
  set: (args: P, value: T | null, path: ParentIndex) => P;
  get: (args: P, path: ParentIndex) => T | null;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  modalVisible: boolean;
  setModalVisible: (value: boolean) => void;
  options: DropdownValue<T>[];
  type: 'buttonModal';
}
export type NavigateDeclarationItem<P extends VisualizationArgsType> = {
  type: 'navigate';
  navigateTo: string;
  label: string;
  icon?: IconType;
  isVisible?: (args: P, path: ParentIndex) => boolean;
  isDisabled?: (args: P, path: ParentIndex) => boolean;
};

export type ArgsItem<P extends VisualizationArgsType = VisualizationArgsType> =
  | NavigateDeclarationItem<P>
  | SwitchDeclarationItem<P>
  | SliderDeclarationItem<P>
  | ButtonDeclarationItem<P>
  | InputDeclarationItem<P>
  | EnumListDeclarationItem<P>
  | ListDeclarationItem<P>
  | DropdownDeclarationItem<P>
  | DropdownAndButtonDeclarationItem<P>
  | ButtonModal<P>;

export type BaseTrace = { name: string; type: string };

type TraceConfigItem<P extends VisualizationArgsType<T>, T extends BaseTrace = BaseTrace> = Omit<
  EnumListDeclarationItem<P>,
  | 'type'
  | 'minNumInList'
  | 'dropdownLabel'
  | 'getLength'
  | 'getEnumType'
  | 'getTitle'
  | 'setTitle'
  | 'onDelete'
  | 'id'
  | 'getOptions'
>;

export interface PageConfigItem<P extends VisualizationArgsType> {
  label: string;
  pageId: string;
  args: ArgsItem<P>[];
}

type MultiTraceConfig<P extends VisualizationArgsType> = Partial<
  Record<P['traces'][0]['type'], P['traces'][0]['type'][]>
>;
export interface VisualizationEditorConfig<P extends VisualizationArgsType> {
  traceDeclaration: TraceConfigItem<P>;
  pages?: PageConfigItem<P>[];
  multiTrace?: MultiTraceConfig<P>;
}

export interface Visualization<P extends VisualizationArgsType = VisualizationArgsType> {
  defaultArgs: NotFunction<P>;
  editorConfig: VisualizationEditorConfig<P>;
  type: string;
  typeDisplayName: string;
  visualization: React.FC<VisualizationArgs<NotFunction<P>>>;
}

export type VisualizationSettings<C extends Visualization = Visualization> = {
  args: C['defaultArgs'];
  type: C['type'];
  sources: GraphModuleItem[];
};

export type GraphModuleItem =
  | SymbolSearchItem
  | WatchlistSearchItem
  | LinkingSearchItem
  | ScannerSearchItem
  | VerticalSearchItem
  | HoldingSearchItem;
export const isGraphModuleItem = (item: SearchItem): item is GraphModuleItem =>
  isSymbolSearchItem(item) ||
  isHoldingSearchItem(item) ||
  isWatchlistSearchItem(item) ||
  isLinkingSearchItem(item) ||
  isVerticalSearchItem(item) ||
  isScannerSearchItem(item);
